<template>
  <v-dialog
    v-model="editDialog"
    persistent
    max-width="480px"
    transition="dialog-bottom-transition"
    style="z-index:200000001;"
  >
    <v-card class="modern-card" elevation="24">
      <!-- Header with gradient background -->
      <div class="header-section">
        <div class="header-content">
          <div class="header-text">
            <h2 class="dialog-title">Edit Company Profile</h2>
            <p class="dialog-subtitle">Update your company logo and information</p>
          </div>
          <v-btn 
            icon 
            @click="onClose" 
            class="close-btn"
            size="small"
          >
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <v-card-text class="content-section">
        <!-- Company Logo Section -->
        <div class="logo-section mt-3">
          <div class="section-label">
            <v-icon class="section-icon" color="#084468">mdi-domain</v-icon>
            Company Logo
          </div>
          <div class="avatar-container">
            <div class="avatar-wrapper">
              <editable-avatar
                :image="profile.midThumbnail ? profile.midThumbnail : profile.image"
                :editable="true"
                :onImageSelected="onImageSelected"
                class="modern-avatar"
                ref="editableAvatar"
              />
              <div class="avatar-overlay" @click="triggerImageUpload">
                <v-icon color="white" size="24">mdi-camera</v-icon>
              </div>
            </div>
          </div>
          <p class="avatar-hint">Click to upload your company logo</p>
        </div>

        <!-- Company Information Section -->
        <div class="company-section">
          <div class="section-label">
            <v-icon class="section-icon" color="#084468">mdi-office-building</v-icon>
            Company Information
          </div>
          
          <div class="input-group">
            <label class="input-label">Company Name</label>
            <v-text-field
              v-model="profile.name"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              class="modern-input"
              :rules="[rules.required]"
              @input="onChange"
              placeholder="Enter your company name"
            />
          </div>
        </div>

        <!-- Error Message -->
        <v-expand-transition>
          <div v-if="message" class="error-section">
            <v-alert
              type="error"
              variant="tonal"
              density="compact"
              class="modern-alert"
            >
              {{ message }}
            </v-alert>
          </div>
        </v-expand-transition>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="action-section">
        <v-btn
          variant="outlined"
          color="grey-darken-1"
          @click="onClose"
          class="cancel-btn"
          :disabled="loading"
        >
          Cancel
        </v-btn>
        <v-btn
          color="#084468"
          @click="onSave"
          class="save-btn"
          :loading="loading"
          elevation="2"
        >
          <v-icon left color="#fff">mdi-content-save</v-icon>
          Save Changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
import EditableAvatar from "@/components/EditableAvatar.vue";

export default {
  components: { EditableAvatar },
  props: {
    editDialog: {
      type: Boolean,
      default: false
    },
    selected: Object,
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      code: "",
      name: "",
      message: null,
      loading: false,
      sendingCode: false,
      disableResend: false,
      resendCount: 0,
      rules: {
        required: value => !!value || "Company name is required"
      },
      selectedImage: null,
      profile: {}
    };
  },
  methods: {
    ...mapActions("company", {
      updateCompany: "updateCompany"
    }),
    onChange() {
      this.message = null;
    },
    onSave() {
      if (!this.profile.image && !this.selectedImage) {
        this.message = "Please select a company logo.";
        return;
      }
      if (!this.profile.name?.trim()) {
        this.message = "Please enter your company name.";
        return;
      }

      this.message = null;
      const formData = new FormData();
      
      if (this.selectedImage) {
        formData.append("image", this.selectedImage);
      }
      formData.append("_id", this.profile._id);
      formData.append("name", this.profile.name.trim());

      this.loading = true;
      this.updateCompany(formData)
        .then(updatedCompany => {
          this.loading = false;
          this.$emit("company-updated", updatedCompany);
          this.onClose();
        })
        .catch(error => {
          this.loading = false;
          if (error.response) {
            this.message = error.response.data.message;
          } else {
            this.message = "An error occurred. Please try again.";
          }
        });
    },
    onImageSelected(image) {
      this.selectedImage = image;
      this.message = null;
    },
    triggerImageUpload() {
      // Trigger the file input click from the EditableAvatar component
      if (this.$refs.editableAvatar && this.$refs.editableAvatar.$el) {
        const fileInput = this.$refs.editableAvatar.$el.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.click();
        }
      }
    }
  },
  watch: {
    selected: {
      handler(newValue) {
        if (newValue) {
          this.profile = { ...newValue };
        }
      },
      immediate: true
    }
  },
  mounted() {
    if (this.selected) {
      this.profile = { ...this.selected };
    }
  }
};
</script>

<style scoped>
.modern-card {
  border-radius: 24px !important;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
}

.header-section {
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  padding: 24px;
  position: relative;
  overflow: hidden;
}

.header-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  opacity: 0.9;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  z-index: 1;
}

.header-text {
  flex: 1;
}

.dialog-title {
  font-family: 'Inter', 'Poppins-SemiBold', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: white;
  margin: 0 0 4px 0;
  line-height: 1.2;
}

.dialog-subtitle {
  font-family: 'Inter', 'Poppins-Regular', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.4;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: scale(1.05);
}

.content-section {
  padding: 32px 24px 24px 24px;
}

.logo-section {
  margin-bottom: 32px;
}

.section-label {
  display: flex;
  align-items: center;
  font-family: 'Inter', 'Poppins-Medium', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 16px;
}

.section-icon {
  margin-right: 8px;
}

.avatar-container {
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
}

.avatar-wrapper {
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.avatar-wrapper:hover {
  transform: scale(1.05);
}

.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(8, 68, 104, 0.85);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

.avatar-wrapper:hover .avatar-overlay {
  opacity: 1;
  pointer-events: auto;
}

.avatar-hint {
  text-align: center;
  font-family: 'Inter', 'Poppins-Regular', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
  color: #6b7280;
  margin: 0;
}

.company-section {
  margin-bottom: 24px;
}

.input-group {
  margin-top: 16px;
}

.input-label {
  display: block;
  font-family: 'Inter', 'Poppins-Medium', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.modern-input :deep(.v-field) {
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: 'Inter', 'Poppins-Regular', -apple-system, BlinkMacSystemFont, sans-serif;
}

.modern-input :deep(.v-field:hover) {
  border-color: #084468;
  background: #ffffff;
}

.modern-input :deep(.v-field--focused) {
  border-color: #084468;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
}

.modern-input :deep(.v-field__input) {
  font-family: 'Inter', 'Poppins-Regular', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  color: #1f2937;
  padding: 12px 16px;
}

.modern-input :deep(.v-field__input::placeholder) {
  color: #9ca3af;
  opacity: 1;
}

.error-section {
  margin-top: 16px;
}

.modern-alert {
  border-radius: 12px;
  border: none;
  font-family: 'Inter', 'Poppins-Regular', -apple-system, BlinkMacSystemFont, sans-serif;
}

.action-section {
  padding: 24px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.cancel-btn {
  border-radius: 12px;
  font-family: 'Inter', 'Poppins-Medium', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 500;
  text-transform: none;
  padding: 12px 24px;
  border: 1px solid #d1d5db;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.cancel-btn:hover {
  border-color: #9ca3af;
  background: #f9fafb;
}

.save-btn {
  border-radius: 12px;
  font-family: 'Inter', 'Poppins-Medium', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 500;
  text-transform: none;
  padding: 12px 24px;
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 140px;
  color: #ffff !important;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px -8px rgba(42, 129, 251, 0.5);
}

.save-btn:active {
  transform: translateY(0);
}

/* Responsive Design */
@media (max-width: 600px) {
  .modern-card {
    margin: 16px;
    border-radius: 16px !important;
  }
  
  .header-section {
    padding: 20px;
  }
  
  .dialog-title {
    font-size: 20px;
  }
  
  .content-section {
    padding: 24px 20px;
  }
  
  .action-section {
    padding: 20px;
    flex-direction: column;
  }
  
  .cancel-btn, .save-btn {
    width: 100%;
  }
}

/* Dialog transition improvements */
.v-dialog {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Custom scrollbar */
.v-dialog::-webkit-scrollbar {
  width: 6px;
}

.v-dialog::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

.v-dialog::-webkit-scrollbar-thumb {
  background: #084468;
  border-radius: 3px;
  transition: background 0.3s ease;
}

.v-dialog::-webkit-scrollbar-thumb:hover {
  background: #084468;
}

/* Loading state improvements */
.save-btn :deep(.v-btn__loader) {
  color: white;
}

/* Focus states */
.modern-input :deep(.v-field--focused .v-field__outline) {
  border-color: #084468;
  border-width: 2px;
}

/* Animation for form validation */
.modern-alert {
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Company-specific styling */
.avatar-wrapper {
  border: 3px solid  rgba(1, 73, 240, 0.3);;
  border-radius: 50%;
  padding: 4px;
}

.avatar-wrapper:hover {
  border-color: rgba(1, 73, 240, 0.3);
}

/* Enhanced company logo section */
.logo-section {
  background: linear-gradient(135deg, rgba(5, 150, 105, 0.05) 0%, rgba(4, 120, 87, 0.05) 100%);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(5, 150, 105, 0.1);
}

.company-section {
  background: rgba(248, 250, 252, 0.5);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e2e8f0;
}
</style>